import { useState } from "react";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import {
  ArrowRightAlt as ArrowRightAltIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { INITIATIVE_FIELDS } from "../domain/initiativeFields";
import { formatFieldValue } from "../utils/formatFieldValue";

export default function InitiativeChangelogEntry({ entry }) {
  const [showDiff, setShowDiff] = useState(false);

  const isSimpleEntry = entry.field === "created" || entry.field === "archived";
  const isCommentEntry = entry.field === "comment";
  const isCommentDeleted = isCommentEntry && entry.reason === "Comment deleted";
  const isCommentAdded = isCommentEntry && entry.reason === "Comment added";
  const isLongText = INITIATIVE_FIELDS[entry.field]?.type === "text";
  const isListDiff =
    Array.isArray(entry.oldValue) || Array.isArray(entry.newValue);
  const label =
    entry.field === "comment"
      ? "Comment"
      : INITIATIVE_FIELDS[entry.field]?.label || entry.field;

  const commentActionLabel = isCommentDeleted
    ? "Deleted"
    : isCommentAdded
      ? "Added"
      : "Changed";
  const commentSingleValue = isCommentDeleted
    ? entry.oldValue
    : isCommentAdded
      ? Array.isArray(entry.newValue)
        ? entry.newValue[0]
        : entry.newValue
      : null;

  const added = isListDiff
    ? (entry.newValue || []).filter((v) => !(entry.oldValue || []).includes(v))
    : [];
  const removed = isListDiff
    ? (entry.oldValue || []).filter((v) => !(entry.newValue || []).includes(v))
    : [];

  const styles = {
    toggleRow: {
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      cursor: "pointer",
    },
    expandIcon: (expanded) => ({
      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.2s",
    }),
    entryTitle: { fontWeight: 500 },
    entryValues: { color: "background.textMuted", overflowWrap: "anywhere" },
    entryListItem: { color: "background.textMuted", overflowWrap: "anywhere", pl: 2 },
    entryArrowIcon: {
      color: "background.text",
      verticalAlign: "middle",
      fontSize: "1.2rem",
    },
  };

  return (
    <Box>
      {isSimpleEntry ? (
        <Typography variant="body2" sx={styles.entryTitle}>
          {entry.reason}
        </Typography>
      ) : isCommentEntry ? (
        <>
          <Box
            sx={styles.toggleRow}
            onClick={() => setShowDiff((prev) => !prev)}
          >
            <Typography variant="body2" sx={styles.entryTitle}>
              {commentActionLabel} {label}
            </Typography>
            <IconButton size="small" sx={styles.expandIcon(showDiff)}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
          <Collapse in={showDiff}>
            <Typography variant="body2" sx={styles.entryValues}>
              {commentSingleValue !== null ? (
                formatFieldValue(commentSingleValue)
              ) : (
                <>
                  {formatFieldValue(entry.oldValue)}{" "}
                  <ArrowRightAltIcon sx={styles.entryArrowIcon} />{" "}
                  {formatFieldValue(entry.newValue)}
                </>
              )}
            </Typography>
          </Collapse>
        </>
      ) : isListDiff ? (
        <>
          {added.length > 0 && (
            <Box>
              <Typography variant="body2" sx={styles.entryTitle}>
                Added {label}
              </Typography>
              {added.map((item, i) => (
                <Typography key={i} variant="body2" sx={styles.entryListItem}>
                  • {item}
                </Typography>
              ))}
            </Box>
          )}
          {removed.length > 0 && (
            <Box>
              <Typography variant="body2" sx={styles.entryTitle}>
                Removed {label}
              </Typography>
              {removed.map((item, i) => (
                <Typography key={i} variant="body2" sx={styles.entryListItem}>
                  • {item}
                </Typography>
              ))}
            </Box>
          )}
        </>
      ) : isLongText ? (
        <>
          <Box
            sx={styles.toggleRow}
            onClick={() => setShowDiff((prev) => !prev)}
          >
            <Typography variant="body2" sx={styles.entryTitle}>
              Changed {label}
            </Typography>
            <IconButton size="small" sx={styles.expandIcon(showDiff)}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
          <Collapse in={showDiff}>
            <Typography variant="body2" sx={styles.entryValues}>
              {formatFieldValue(entry.oldValue)}{" "}
              <ArrowRightAltIcon sx={styles.entryArrowIcon} />{" "}
              {formatFieldValue(entry.newValue)}
            </Typography>
          </Collapse>
        </>
      ) : (
        <>
          <Typography variant="body2" sx={styles.entryTitle}>
            Changed {label}
          </Typography>
          <Typography variant="body2" sx={styles.entryValues}>
            {formatFieldValue(entry.oldValue)}{" "}
            <ArrowRightAltIcon sx={styles.entryArrowIcon} />{" "}
            {formatFieldValue(entry.newValue)}
          </Typography>
          {entry.message && (
            <Typography variant="body2" sx={styles.entryValues}>
              {entry.message}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
